import { useMemo } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { Box, Stack } from "@mui/system";
import { t } from "i18next";
import { Autocomplete, TextField, Typography } from "@mui/material";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { formatNumber, fPercent } from "src/utils/format-number";
import GreenCheckSwitch from "../components/GreenCheckSwitch";
import ActivationCell from "../data-grid-keyword-exact/components/ActivationCell";
import { formatCurrency } from "src/utils/format-currency";
import PercentageChange from "../components/PercentageChange";
import { palette } from "src/sections/overview/dashboards/views/non-branded/figma-palette";
import PresenceRateChartNew from "src/components/visibility/components/PresenceRateChartNew";

export const useKeywordExactColumns = ({
    workspace,
    lang,
    handleToggle,
    selectedCompareDate,
    setModalActivationManual,
    setModalActivationAuto,
    setConfirmDisable,
    setModalCreateStrategy,
}: any): GridColDef[] => {

    return useMemo(
        () => [
            {
                field: "keyword",
                headerName: t("dashboard.global.keyword"),
                disableColumnMenu: true,
                minWidth: 285,
                headerClassName: "keyword-header",
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography
                            fontWeight={600}
                            color="rgba(99, 115, 129, 1)"
                            fontSize={14}
                        >
                            {t("dashboard.global.keyword")}
                        </Typography>

                        <CustomTooltip title={"Ad clicks"}
                            placement="top"
                            arrow>
                            <Iconify
                                icon="proicons:cursor-click"
                                width={20}
                                sx={{ cursor: 'help' }}
                                height={20}
                            />
                        </CustomTooltip>
                    </Box>
                ),
                renderCell: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            height: "60px",
                            padding: "0px 12px 0px 8px",
                            gap: 3
                        }}
                    >
                        <CustomTooltip title={params.row.keyword} arrow>
                            <Typography
                                fontSize={14}
                                color="rgba(51, 60, 71, 1)"
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    cursor: 'help'
                                }}
                            >
                                [{params.row.keyword}]
                            </Typography>
                        </CustomTooltip>

                        <Typography
                            fontSize={14}
                            color="rgba(99, 115, 129, 1)"
                            sx={{
                                marginLeft: "auto",
                                textAlign: "right",
                            }}
                        >
                            {formatNumber(params.row.clicks)}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: "keyword_id",
                valueGetter: (params) => {
                    return String(params).replace(/\s+/g, '');
                },
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Keyword ID
                        </Typography>
                    </Box>
                ),
                headerName: "Keyword ID",
                minWidth: 200,
                type: "number",
                sortable: false,
                disableColumnMenu: true,
            },
            {
                field: "platform",
                headerName: "Platform",
                minWidth: 140,
                sortable: true,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Platform
                        </Typography>
                    </Box>
                ),
                type: "singleSelect",
                valueOptions: [
                    { value: "GOOGLE", label: "Google Ads" },
                    { value: "BING", label: "Bing Ads" },
                ],

                valueGetter: (params: any) => (params || "").toUpperCase(),

                renderCell: (params) => {
                    const map: any = {
                        GOOGLE: "Google Ads",
                        BING: "Bing Ads",
                    };

                    return map[params.value] || params.value;
                },
            },
            // Campaigns
            {
                field: "campaign_names",
                sortable: true,
                disableColumnMenu: true,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Campaign
                        </Typography>
                    </Box>
                ),
                headerName: "Campaign",
                minWidth: 160,
                renderCell: ({ value }) => {
                    return (
                        <CustomTooltip
                            title={value?.join(", ")}
                            placement="top"
                            arrow
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        px: "12px",
                                        py: "8px",
                                        borderRadius: "8px",
                                        backgroundColor: "#FCFCFD",
                                        boxShadow: "1px 3px 8px 0 rgba(0, 0, 0, 0.08)",
                                    },
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    height: "60px",
                                    px: "12px",
                                    minWidth: 0,
                                    overflow: "hidden",
                                }}
                            >
                                <Typography
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: '14px',
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        color: "rgba(51, 60, 71, 1)",
                                        cursor: 'help'
                                    }}
                                >
                                    {(value || []).join(", ")}
                                </Typography>
                            </Box>
                        </CustomTooltip>
                    );
                }
            },
            {
                field: "campaign_ids",
                type: 'number',
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Campaign ID
                        </Typography>
                    </Box>
                ),
                disableColumnMenu: true,
                headerName: "Campaign ID",
                flex: 1,
                sortable: false
            },
            {
                field: "ad_group_names",
                disableColumnMenu: true,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Ad Group
                        </Typography>
                    </Box>
                ),
                headerName: "Ad Group Name",
                minWidth: 200,
                sortable: true
            },
            {
                field: "ad_group_ids",
                type: 'number',
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Ad Group ID
                        </Typography>
                    </Box>
                ),
                disableColumnMenu: true,
                headerName: "Ad Group ID",
                flex: 1,
                sortable: false
            },
            // Scraping Status
            {
                field: "scraping_status",
                headerName: "Scraping Status",
                sortable: false,
                disableColumnMenu: true,
                filterable: true,
                width: 85,
                type: "boolean",
                minWidth: 85,
                maxWidth: 85,
                renderHeader: () => (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                        }}
                    >
                        <Typography fontWeight={600} fontSize={14} color="rgba(99,115,129,1)">
                            Scraping
                        </Typography>
                        <Typography fontWeight={600} fontSize={14} color="rgba(99,115,129,1)">
                            Status
                        </Typography>
                    </Box>
                ),

                renderCell: ({ row }) => {
                    const activeDetail = row.scraping_details?.find((s: any) => s.status === true);
                    const freq = activeDetail?.frequency;

                    const content = (
                        <Box
                            sx={{
                                width: "100%",
                                height: "60px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                padding: "0px 12px",
                            }}
                        >
                            <GreenCheckSwitch
                                checked={!!activeDetail}
                                onChange={() => handleToggle(row, !!activeDetail)}
                            />
                        </Box>
                    );

                    if (!activeDetail) return content;

                    return (
                        <CustomTooltip
                            title={`Frequency: ${freq || "N/A"}`}
                            placement="top"
                            arrow
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        px: "12px",
                                        py: "8px",
                                        borderRadius: "8px",
                                        backgroundColor: "#FCFCFD",
                                        boxShadow: "0px 3px 8px rgba(0,0,0,0.08)",
                                    },
                                },
                            }}
                        >
                            {content}
                        </CustomTooltip>
                    );
                },
            },
            {
                field: "mode",
                headerName: "Mode",
                disableColumnMenu: true,
                filterable: true,
                sortable: false,
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                            flexDirection: "column"
                        }}
                    >
                        <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                            Mode
                        </Typography>
                    </Box>
                ),
                minWidth: 100,
                maxWidth: 190,
                width: 190,
                editable: false,
                filterOperators: [
                    {
                        label: "is any of",
                        value: "isAnyOf",
                        getApplyFilterFn: () => null,

                        InputComponent: (props) => {
                            const { item, applyValue } = props;

                            return (
                                <Autocomplete
                                    sx={{ mt: 2.5 }}
                                    multiple
                                    options={[
                                        "Active Mode",
                                        "KO Bidding Manual",
                                        "KO Bidding Auto",
                                    ]}
                                    value={item.value || []}
                                    onChange={(event, newValue) => {
                                        applyValue({
                                            ...item,
                                            value: newValue,
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="standard"
                                            placeholder="Select modes"
                                        />
                                    )}
                                    size="small"
                                />
                            );
                        },
                    },
                ],
                renderCell: (params) => (
                    <ActivationCell
                        params={params}
                        setModalActivationManual={setModalActivationManual}
                        setModalActivationAuto={setModalActivationAuto}
                        setConfirmDisable={setConfirmDisable}
                        setModalCreateStrategy={setModalCreateStrategy}
                    />
                )
            },
            {
                disableColumnMenu: true,
                field: "max_cpc",
                filterable: false,
                type: "string",
                sortable: false,
                headerAlign: 'left',
                headerName: "Max CPC",

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        fontSize={14}
                        color="rgba(99, 115, 129, 1)"
                        sx={{
                            whiteSpace: "normal",
                            lineHeight: 1.2,
                        }}
                    >
                        Set Max CPC
                    </Typography>
                ),

                minWidth: 90,
                editable: true,
                renderEditCell: (params) => {
                    return (
                        <Box
                            display="flex"
                            width="100%"
                            height="100%"
                            alignItems="center"
                            justifyContent="flex-end"
                            px={1}
                        >
                            <TextField
                                autoFocus
                                value={params.value ?? ""}
                                onChange={(event) => {
                                    let value = event.target.value;

                                    value = value.replace(",", ".");

                                    params.api.setEditCellValue({
                                        id: params.id,
                                        field: params.field,
                                        value,
                                    });
                                }}
                                variant="outlined"
                                size="small"
                                fullWidth
                                inputProps={{
                                    style: { textAlign: "right" },
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        textAlign: "right",
                                        "& fieldset": {
                                            borderRadius: "6px",
                                        },
                                        "&:hover fieldset": {
                                            border: "1px solid var(--color-azure-58, #338AF3)",
                                            background: "rgba(255, 255, 255, 0.01)",
                                            boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            border: "1px solid var(--color-azure-58, #338AF3)",
                                            boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                        },
                                    },
                                    "& input": {
                                        textAlign: "right",
                                        cursor: "pointer",
                                    },
                                }}
                            />
                        </Box>
                    );
                },

                renderCell: (params: any) => {
                    const activations = params.row.activations_details || [];

                    const koManual = activations.find(
                        (d: any) => d.status === true && d.mode === "KO Bidding Manual"
                    );

                    const koAuto = activations.find(
                        (d: any) => d.status === true && d.mode?.startsWith("KO Bidding Auto")
                    );

                    const value =
                        params.row.max_cpc !== undefined
                            ? params.row.max_cpc
                            : koManual?.max_cpc;

                    const isAuto = !koManual && Boolean(koAuto);
                    const isActive = Boolean(koManual);

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                width: "100%",
                                height: "100%",
                                boxSizing: "border-box",
                                minWidth: 0,
                            }}
                        >
                            {isActive ? (
                                isAuto ? (
                                    <span style={{ opacity: 0.5, width: 52, textAlign: "right", display: "inline-block" }}>
                                        {params?.row?.max_cpc}
                                    </span>
                                ) : (
                                    <Box
                                        onClick={() => {
                                            params.api.startCellEditMode({
                                                id: params.id,
                                                field: params.field,
                                            });
                                        }}
                                        sx={{
                                            width: 80,
                                            height: 36,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                            px: "10px",
                                            borderRadius: "6px",
                                            border: "1px solid rgba(145, 158, 171, 0.20)",
                                            background: "rgba(255, 255, 255, 0.01)",
                                            fontSize: "0.875rem",
                                            lineHeight: 1,
                                            whiteSpace: "nowrap",
                                            boxSizing: "border-box",
                                            cursor: "pointer",
                                            transition: "all 0.15s ease",
                                            "&:hover": {
                                                border: "1px solid var(--color-azure-58, #338AF3)",
                                                boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.35)",
                                            },
                                        }}
                                    >
                                        {formatCurrency(value, workspace?.currency, lang, true, 2) ?? ""}
                                    </Box>
                                )
                            ) : (
                                <Typography
                                    sx={{
                                        width: 52,
                                        textAlign: "right",
                                        fontFamily: '"Public Sans", sans-serif',
                                        fontSize: "14px",
                                        fontWeight: 400,
                                        lineHeight: "14px",
                                        color: "#adb1b5",
                                        pr: 1.2,
                                    }}
                                >
                                    {formatCurrency(params.row.max_cpc_3, workspace?.currency, lang, true, 2) ?? ""}
                                </Typography>
                            )}
                        </Box>
                    );
                },
            },
            {
                field: "max_cpc_2",
                sortable: true,
                headerAlign: "left",
                disableColumnMenu: true,
                filterable: true,
                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        fontSize={14}
                        color="rgba(99, 115, 129, 1)"
                        sx={{
                            whiteSpace: "normal",
                            lineHeight: 1.2,
                        }}
                    >
                        Max CPC Evol
                    </Typography>
                ),
                headerName: "Max CPC Evol",
                renderCell: ({ row }) => {
                    const currentMaxCpc = row.max_cpc_2 ?? 0;
                    const compareMaxCpc = row?.max_cpc_compare ?? 0;

                    let percentageChange = null;

                    if (compareMaxCpc > 0) {
                        percentageChange =
                            ((currentMaxCpc - compareMaxCpc) / compareMaxCpc) * 100;
                    }

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                height: 60,
                                px: "12px",
                                boxSizing: "border-box",
                                gap: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "#333C47",
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                }}
                            >
                                {formatCurrency(currentMaxCpc, workspace?.currency, lang, true, 2) ?? ""}
                            </Typography>

                            <Box
                                sx={{
                                    width: 50,
                                    textAlign: "right",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <PercentageChange value={percentageChange} inverse />
                            </Box>
                        </Box>
                    );
                },
                minWidth: 144,
                type: "number",
            },
            {
                field: "cost",
                disableColumnMenu: true,
                sortable: true,
                headerAlign: "left",
                filterable: true,

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        color="rgba(99, 115, 129, 1)"
                        fontSize={14}
                    >
                        Ad Cost
                    </Typography>
                ),

                headerName: "Ad Cost",

                renderCell: ({ row }) => {
                    const currentCost = row.cost ?? 0;
                    const compareCost = row?.cost_compare ?? 0;

                    let percentageChange = null;

                    if (compareCost > 0) {
                        percentageChange =
                            ((currentCost - compareCost) / compareCost) * 100;
                    }

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                height: 60,
                                px: "12px",
                                boxSizing: "border-box",
                                gap: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "#333C47",
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                }}
                            >
                                {formatCurrency(
                                    currentCost,
                                    workspace?.currency,
                                    lang,
                                    true,
                                    0
                                )}
                            </Typography>

                            <Box
                                sx={{
                                    width: 50,
                                    textAlign: "right",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                }}
                            >
                                <PercentageChange value={percentageChange} inverse={true} />
                            </Box>
                        </Box>
                    );
                },
                minWidth: 144,
                type: "number",
            },
            // AVG. CPC
            {
                field: "avg_cpc",
                disableColumnMenu: true,
                sortable: true,
                headerAlign: "left",
                filterable: true,

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        color="rgba(99, 115, 129, 1)"
                        fontSize={14}
                    >
                        Avg. CPC
                    </Typography>
                ),

                headerName: "Avg. CPC",

                renderCell: ({ row }) => {
                    const currentCpc = row.avg_cpc ?? 0;
                    const compareCpc = row?.avg_cpc_compare ?? 0;

                    let percentageChange = null;

                    if (compareCpc > 0) {
                        percentageChange =
                            ((currentCpc - compareCpc) / compareCpc) * 100;
                    }

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                height: 60,
                                px: "12px",
                                boxSizing: "border-box",
                                gap: "8px",
                                width: "100%",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "#333C47",
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                }}
                            >
                                {formatCurrency(
                                    currentCpc,
                                    workspace?.currency,
                                    lang,
                                    true,
                                    2
                                )}
                            </Typography>

                            <Box
                                sx={{
                                    width: 50,
                                    textAlign: "right",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                }}
                            >
                                <PercentageChange value={percentageChange} inverse />
                            </Box>
                        </Box>
                    );
                },

                minWidth: 140,
                type: "number",
            },
            // BLENDED CTR
            {
                field: "blended_ctr",
                sortable: true,
                disableColumnMenu: true,
                filterable: true,
                headerAlign: "left",
                renderHeader: (params) => (

                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Blended CTR
                    </Typography>
                ),
                headerName: "Blended CTR",
                renderCell: (params: GridRenderCellParams) => {
                    const rawValue = params?.row?.blended_ctr || 0;
                    const rawCompare = params?.row?.blended_ctr_compare || 0;

                    const currentCTR = rawValue * 100;
                    const compareCTR = rawCompare * 100;

                    const color = palette.visibility_range.find(({ min, max }) => {
                        return currentCTR >= min && currentCTR <= max;
                    })?.color;

                    let percentageChange: number | null = null;

                    if (compareCTR !== 0) {
                        percentageChange = currentCTR - compareCTR;
                    }

                    return (
                        <Stack
                            direction="row"
                            alignItems="center"
                            sx={{
                                width: "100%",
                                height: "60px",

                                px: 0.5,
                                gap: 1,
                                boxSizing: "border-box",

                            }}
                        >
                            <Typography
                                sx={{
                                    width: 30,
                                    flexShrink: 0,
                                    color: "#333C47",
                                    fontVariantNumeric: "tabular-nums",
                                    fontSize: 14,
                                }}
                            >
                                {currentCTR.toFixed(0)}%
                            </Typography>

                            <Box
                                sx={{
                                    height: 8,
                                    backgroundColor: "transparent", // 👈 fond visible (track)
                                    borderRadius: "6px",
                                    overflow: "visible", // 👈 IMPORTANT pour ne pas couper les bordures
                                    mx: 0.5,
                                    minWidth: 10,
                                    position: "relative",
                                    width: "100%"
                                }}
                            >
                                <Box
                                    sx={{
                                        width: `${Math.min(Math.max(currentCTR, 0), 100)}%`,
                                        height: "100%",
                                        backgroundColor: color,

                                        borderLeft: "2px solid rgba(0,0,0,0.15)",
                                        borderRight: "2px solid rgba(0,0,0,0.15)",
                                        boxSizing: "border-box",

                                        borderRadius: "6px",
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    flexShrink: 0,
                                    textAlign: "right",
                                }}
                            >
                                <PercentageChange
                                    format="pp"
                                    value={percentageChange}
                                    inverse={false}
                                />
                            </Box>
                        </Stack>
                    );
                },
                minWidth: 212,
                type: "number"
            },
            {
                field: "blended_cpc",
                sortable: true,
                disableColumnMenu: true,
                headerAlign: "left",
                filterable: true,

                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        color="rgba(99, 115, 129, 1)"
                        fontSize={14}
                    >
                        Blended CPC
                    </Typography>
                ),

                headerName: "Blended CPC",

                renderCell: ({ row }) => {
                    const currentCpc = row.blended_cpc ?? 0;
                    const compareCpc = row?.blended_cpc_compare ?? 0;

                    let percentageChange = null;

                    if (compareCpc > 0) {
                        percentageChange =
                            ((currentCpc - compareCpc) / compareCpc) * 100;
                    }

                    return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                height: 60,
                                px: "12px",
                                boxSizing: "border-box",
                                gap: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: "#333C47",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                }}
                            >
                                {formatCurrency(
                                    currentCpc,
                                    workspace?.currency,
                                    lang,
                                    true,
                                    2
                                )}
                            </Typography>

                            <Box
                                sx={{
                                    width: 50,
                                    textAlign: "right",
                                    fontVariantNumeric: "lining-nums tabular-nums",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                }}
                            >
                                <PercentageChange value={percentageChange} inverse />
                            </Box>
                        </Box>
                    );
                },

                minWidth: 144,

                type: "number",
            },
            {
                field: 'visibility_rate',
                disableColumnMenu: true,
                headerAlign: "left",
                headerName: 'Visibility rate',
                type: 'number',
                flex: 1,
                minWidth: 225,
                renderHeader: () => (
                    <Typography
                        fontWeight={600}
                        color="rgba(99, 115, 129, 1)"
                        fontSize={14}
                    >
                        Visibility Rate
                    </Typography>
                ),

                valueGetter: (params: any) => params ?? 0,

                renderCell: (params: GridRenderCellParams) => {
                    const data = {
                        organicOnly: params?.row?.organic_rate || 0,
                        soloBidder: Number(params?.row?.solo_bidder) || 0,
                        topAdPosition: Number(params?.row?.top_ad) || 0,
                        competitorOnly: Number(params?.row?.competitor_only) || 0,
                    };

                    const currentVisibilityRate = params?.row?.visibility_rate || 0;
                    const compareVisibilityRate = params?.row?.visibility_rate_compare || 0;

                    const delta = compareVisibilityRate - currentVisibilityRate;

                    return (
                        <Stack
                            direction="row"
                            alignItems="center"
                            sx={{
                                width: "100%",
                                height: 60,
                                gap: 1,
                                overflow: "hidden",
                                minWidth: 0,
                                boxSizing: "border-box",
                            }}
                        >

                            {/* LEFT VALUE */}
                            <Box
                                sx={{
                                    width: 50,
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <Typography variant="body2">
                                    {fPercent(Math.round(currentVisibilityRate))}
                                </Typography>
                            </Box>

                            {/* BAR / CHART */}
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    height: 24,
                                    display: "flex",
                                    alignItems: "center",
                                    overflow: "hidden",
                                }}
                            >
                                <PresenceRateChartNew
                                    round={1}
                                    data={data}
                                    height={40}
                                    totalVisibilityRate={currentVisibilityRate}
                                    colors={[
                                        "#18A155",
                                        "#C09910",
                                        "#F9DE81",
                                        "#e9ecee"
                                    ]}
                                />
                            </Box>

                            {/* RIGHT DELTA */}
                            <Box
                                sx={{
                                    minWidth: 50,
                                    flexShrink: 0,
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                }}
                            >
                                <PercentageChange
                                    value={delta}
                                    format="pp"
                                />
                            </Box>

                        </Stack>
                    );
                },
            }
        ], [handleToggle, workspace?.currency, lang, selectedCompareDate]);
};